import { createContext, useContext, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getWork, addWork, updateWork, deleteWorkItem } from "../../api/carreers";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";
import { useNavigate } from "react-router-dom";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const workArray = useRef();
  const toaster = useToaster();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const getWorkQuery = useQuery({
    queryKey: ["works"],
    queryFn: getWork,
  });
  const addWorkQuery = useMutation({ mutationFn: addWork });
  const updateWorkQuery = useMutation({ mutationFn: updateWork });
  const deleteWorkItemQuery = useMutation({ mutationFn: deleteWorkItem });

  const initialValues = useMemo(() => {
    const workData = getWorkQuery?.data?.data?.[0] || {};
    return {
      id: workData?._id,
      meta: {
        meta_title: workData?.meta_title || "",
        meta_slug_url: workData?.meta_slug_url || "",
        meta_description: workData?.meta_description || "",
        keywords: workData?.keywords || "",
      },
      banner: {
        heading: workData?.heading || "",
        sub_heading: workData?.description || "",
        description: workData?.description || "",
        cta_label: workData?.cta_label || "",
        cta_redirect_link: workData?.cta_redirect_link || "",
        works: workData?.careers?.map((item) => ({ ...item, id: item._id })) || [{ title: "", description: "" }],
      },
    };
  }, [getWorkQuery]);

  const handleSubmit = async (values) => {
    const payload = {
      meta_title: values?.meta?.meta_title || "",
      meta_slug_url: values?.meta?.meta_slug_url || "",
      meta_description: values?.meta?.meta_description || "",
      keywords: values?.meta?.keywords || "",

      heading: values?.banner?.heading || "",
      sub_heading: values?.banner?.sub_heading || "",
      description: values?.banner?.sub_heading || "",
      cta_label: values?.banner?.cta_label || "",
      cta_redirect_link: values?.banner?.cta_redirect_link || "",
    };
    const workData = getWorkQuery?.data?.data?.[0] || {};

    if (workData._id) {
      payload.id = workData._id;
      updateWorkQuery.mutate(payload, {
        onSuccess: () => {
          toaster.success(Const.MESSAGES.SUCCESS, Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY);
        },
      });
      return;
    }
    addWorkQuery.mutate(payload, {
      onSuccess: () => {
        toaster.success(Const.MESSAGES.SUCCESS, Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY);
      },
    });
  };

  const handleWorkAdd = () => {
    workArray.current.push({ heading: "", description: "", image: "" });
  };

  const handleWorkDelete = async (item, values) => {
    deleteWorkItemQuery.mutate(
      { id: values.id, itemId: item.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["works"]);
          toaster.success(Const.MESSAGES.SUCCESS, "Work is deleted successfully.");
        },
        onError: () => {
          toaster.error(Const.MESSAGES.FAILED, "Internal Server Error");
        },
      }
    );
  };

  const handleEditClick = (item, values) => {
    navigate(`/addcareer/${values.id}?itemId=${item.id}`);
  };

  return (
    <PageContext.Provider
      value={{
        initialValues,
        handleWorkDelete,
        handleWorkAdd,
        workArray,
        handleSubmit,
        getWorkQuery,
        handleEditClick,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const useWorkProvider = () => {
  return useContext(PageContext);
};
